/**
 * The persona bar (Ruling 17): demo convenience that never substitutes for real
 * authentication.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * THE RULE THIS COMPONENT EXISTS UNDER
 *   No token is held, minted or swapped by the application, and no credential beyond
 *   the signed-in user's own is ever stored. Choosing a persona ENDS the session here
 *   and at the identity provider, then starts a real OIDC authorization whose
 *   `login_hint` preselects the demo username on the provider's own page. The password,
 *   the consent and the tokens remain entirely the provider's business.
 *
 * The handoff is a sessionStorage hint read after the provider's post-logout redirect
 * lands back on the application's home page.
 */

import { useState } from "react";

import { strings } from "../brand/strings";

/** The sessionStorage key the gate reads to auto-start the hinted authorization. */
export const PERSONA_HINT_KEY = "qualify.persona.hint";

/**
 * The demo accounts the seed plants. Usernames only; a password never appears in this
 * repository, this bundle or this session storage.
 */
export const DEMO_ACCOUNTS: readonly { readonly username: string; readonly role: string }[] = [
  { username: "mahmoud", role: "Platform owner" },
  { username: "hq-management", role: "Higher management" },
  { username: "barakat", role: "QC lead" },
  { username: "pm-1", role: "Project manager" },
  { username: "north-inspector", role: "Inspector" },
  { username: "insp-mech", role: "Inspector" },
  { username: "insp-ei", role: "Inspector" },
  { username: "contract-inspector", role: "Inspector" },
  { username: "tpi-inspector", role: "Third-party inspector" },
  { username: "qa-test", role: "Contractor QC" },
  { username: "beta-qc", role: "Contractor QC" },
  { username: "alpha-qc", role: "Subcontractor QC" },
  { username: "rival-qc", role: "Contractor QC" },
  { username: "vendor-contact", role: "Vendor" },
];

export function PersonaBar({ onSwitch }: { onSwitch: () => void }): React.JSX.Element {
  const [open, setOpen] = useState(false);

  function choose(username: string): void {
    sessionStorage.setItem(PERSONA_HINT_KEY, username);
    onSwitch();
  }

  return (
    <div className="q-persona" data-testid="persona-bar">
      <button
        type="button"
        className="q-button q-button--quiet"
        aria-expanded={open}
        aria-controls="q-persona-list"
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {strings.persona.heading}
      </button>
      {open && (
        <div className="q-persona__list" id="q-persona-list">
          <p className="q-note">{strings.persona.helper}</p>
          {DEMO_ACCOUNTS.map((account) => (
            <button
              key={account.username}
              type="button"
              className="q-persona__account"
              onClick={() => {
                choose(account.username);
              }}
            >
              {strings.persona.switchTo(account.username)}
              <span className="q-persona__role">{account.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
